import React from 'react';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import {
  Box,
  Button,
  ClickAwayListener,
  Dialog,
  DialogContent,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import type { AdditionalQuestionResponseType } from './additionalQuestionConfig';
import { Tag } from '@/components/ui/tag';
import sourceFilterIcon from '@/assets/images/book.svg';

const ALL_SOURCES_ID = 'all';
const SOURCE_FILTER_ICON = sourceFilterIcon;

export interface QuestionnaireCopySource {
  id: string;
  label: string;
}

export interface QuestionnaireCopyQuestionItem {
  id: string;
  sourceQuestionId: string;
  code: string;
  questionText: string;
  sourceQuestionnaireId: string;
  typeLabel: string;
  chips: string[];
  responseType: AdditionalQuestionResponseType;
  required: boolean;
  optionLines: string;
  /** Question ids that must all be selected (from showWhen). */
  dependsOnQuestionIds?: string[];
  /** Question ids of which at least one must be selected (from showWhenAny). */
  dependsOnAnyQuestionIds?: string[];
}

interface QuestionnaireCopyFromSourceDialogProps {
  open: boolean;
  sources: QuestionnaireCopySource[];
  questions: QuestionnaireCopyQuestionItem[];
  isSubmitting?: boolean;
  onClose: () => void;
  onDone: (questions: QuestionnaireCopyQuestionItem[]) => void;
}

export function QuestionnaireCopyFromSourceDialog({
  open,
  sources,
  questions,
  isSubmitting = false,
  onClose,
  onDone,
}: QuestionnaireCopyFromSourceDialogProps) {
  const [selectedSourceId, setSelectedSourceId] = React.useState<string>(ALL_SOURCES_ID);
  const [isSourceDropdownOpen, setIsSourceDropdownOpen] = React.useState(false);
  const [sourceSearchTerm, setSourceSearchTerm] = React.useState('');
  const [selectedQuestionIds, setSelectedQuestionIds] = React.useState<string[]>([]);

  React.useEffect(() => {
    if (!open) return;
    setIsSourceDropdownOpen(false);
    setSourceSearchTerm('');
    setSelectedQuestionIds([]);
    setSelectedSourceId(
      sources.some((source) => source.id === ALL_SOURCES_ID) ? ALL_SOURCES_ID : sources[0]?.id ?? ALL_SOURCES_ID
    );
  }, [open, sources]);

  const selectedIdSet = React.useMemo(() => new Set(selectedQuestionIds), [selectedQuestionIds]);

  const filteredQuestions = React.useMemo(() => {
    return questions.filter((question) => {
      const sourceMatched = selectedSourceId === ALL_SOURCES_ID || question.sourceQuestionnaireId === selectedSourceId;
      return sourceMatched;
    });
  }, [questions, selectedSourceId]);

  const selectedSourceLabel = React.useMemo(() => {
    if (selectedSourceId === ALL_SOURCES_ID) return 'Select Questionnaire';
    return sources.find((source) => source.id === selectedSourceId)?.label ?? 'Select Questionnaire';
  }, [selectedSourceId, sources]);

  const filteredSources = React.useMemo(() => {
    const normalizedSearch = sourceSearchTerm.trim().toLowerCase();
    if (!normalizedSearch) return sources;
    return sources.filter((source) => source.label.toLowerCase().includes(normalizedSearch));
  }, [sourceSearchTerm, sources]);

  const selectedQuestions = React.useMemo(() => {
    if (selectedQuestionIds.length === 0) return [];
    const selectedIds = new Set(selectedQuestionIds);
    return questions.filter((question) => selectedIds.has(question.id));
  }, [questions, selectedQuestionIds]);

  const handleToggleQuestion = React.useCallback((questionId: string) => {
    setSelectedQuestionIds((prev) =>
      prev.includes(questionId) ? prev.filter((id) => id !== questionId) : [...prev, questionId]
    );
  }, []);

  const handleSelectSource = React.useCallback((sourceId: string) => {
    setSelectedSourceId(sourceId);
    setIsSourceDropdownOpen(false);
  }, []);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      PaperProps={{
        sx: {
          width: { xs: 'calc(100% - 24px)', sm: 'min(90vw, 860px)', md: 860 },
          maxWidth: 860,
          maxHeight: 'calc(100vh - 32px)',
          m: { xs: 1.5, sm: 2 },
          borderRadius: '18px',
          bgcolor: '#f8fafc',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', flex: '1 1 auto', minHeight: 0, overflow: 'hidden' }}>
        <Box sx={{ px: { xs: 1.5, sm: 2, md: 2.5 }, pt: { xs: 1.5, sm: 2, md: 2.5 }, pb: { xs: 1.5, sm: 2 }, flexShrink: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.5 }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 600,
                  fontSize: { xs: '18px', sm: '20px', md: '22px' },
                  lineHeight: 1.3,
                  letterSpacing: '-0.26px',
                  color: '#111827',
                }}
              >
                Copy Question from Source
              </Typography>
              <Typography
                sx={{
                  mt: 0.5,
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 400,
                  fontSize: { xs: '12px', sm: '13px', md: '14px' },
                  lineHeight: 1.4,
                  color: '#667085',
                }}
              >
                Questions copied here become editable text fields in your custom form.
              </Typography>
            </Box>

            <IconButton
              onClick={onClose}
              sx={{
                width: { xs: 36, sm: 40 },
                height: { xs: 36, sm: 40 },
                borderRadius: '999px',
                bgcolor: '#eef2f7',
                color: '#98a2b3',
                flexShrink: 0,
                '&:hover': { bgcolor: '#e6ebf2' },
              }}
            >
              <CloseRoundedIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
            </IconButton>
          </Box>

          <ClickAwayListener onClickAway={() => setIsSourceDropdownOpen(false)}>
            <Box sx={{ mt: 1.5 }}>
              <Button
                variant="outlined"
                onClick={() => setIsSourceDropdownOpen((prev) => !prev)}
                endIcon={
                  <ExpandMoreRoundedIcon
                    sx={{
                      fontSize: { xs: 22, sm: 26 },
                      color: '#98a2b3',
                      transform: isSourceDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.2s ease',
                    }}
                  />
                }
                sx={{
                  width: '100%',
                  minHeight: { xs: 48, sm: 56, md: 70 },
                  borderRadius: { xs: '12px', sm: '14px', md: '18px' },
                  borderColor: '#d0d5dd',
                  bgcolor: '#f8fafc',
                  justifyContent: 'space-between',
                  textTransform: 'none',
                  px: { xs: 1.5, sm: 2, md: 2.5 },
                  '&:hover': { borderColor: '#c2c8d0', bgcolor: '#f2f4f7' },
                }}
              >
                <Typography
                  sx={{
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 400,
                    fontSize: { xs: '14px', sm: '16px', md: '18px' },
                    lineHeight: 1.35,
                    color: '#98a2b3',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {selectedSourceLabel || 'Select Questionnaire'}
                </Typography>
              </Button>

              {isSourceDropdownOpen && (
                <Box
                  sx={{
                    mt: 1,
                    borderRadius: { xs: '12px', sm: '14px', md: '18px' },
                    border: '1px solid #e4e7ec',
                    bgcolor: '#fff',
                    boxShadow: '0 10px 28px rgba(15, 23, 42, 0.12)',
                    overflow: 'hidden',
                  }}
                >
                  <Box sx={{ p: 1, borderBottom: '1px solid #e4e7ec' }}>
                    <TextField
                      fullWidth
                      size="small"
                      value={sourceSearchTerm}
                      onChange={(event) => setSourceSearchTerm(event.target.value)}
                      placeholder="Search Questionnaire"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '10px',
                          bgcolor: '#f8fafc',
                          '& fieldset': { borderColor: '#d0d5dd' },
                        },
                        '& input': {
                          fontFamily: 'Inter, sans-serif',
                          fontWeight: 400,
                          fontSize: { xs: '14px', sm: '15px' },
                          lineHeight: 1.4,
                          color: '#667085',
                        },
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchRoundedIcon sx={{ color: '#98a2b3', fontSize: 20 }} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box>

                  <Box sx={{ maxHeight: { xs: 200, sm: 260 }, overflowY: 'auto' }}>
                    {filteredSources.length === 0 ? (
                      <Box sx={{ px: 2, py: 2.5 }}>
                        <Typography
                          sx={{
                            fontFamily: 'Inter, sans-serif',
                            fontWeight: 500,
                            fontSize: '14px',
                            lineHeight: '20px',
                            color: '#98a2b3',
                          }}
                        >
                          No questionnaires found.
                        </Typography>
                      </Box>
                    ) : (
                      filteredSources.map((source, index) => {
                        const selected = selectedSourceId === source.id;

                        return (
                          <Button
                            key={source.id}
                            onClick={() => handleSelectSource(source.id)}
                            startIcon={
                              <Box
                                component="img"
                                src={SOURCE_FILTER_ICON}
                                alt=""
                                aria-hidden="true"
                                sx={{
                                  width: 18,
                                  height: 18,
                                  display: 'block',
                                  objectFit: 'contain',
                                }}
                              />
                            }
                            sx={{
                              width: '100%',
                              minHeight: { xs: 52, sm: 60, md: 66 },
                              borderRadius: 0,
                              justifyContent: 'flex-start',
                              textTransform: 'none',
                              px: { xs: 1.5, sm: 2, md: 2.25 },
                              py: 1.25,
                              borderBottom: index === filteredSources.length - 1 ? 'none' : '1px solid #e4e7ec',
                              bgcolor: selected ? '#f8fbff' : '#fff',
                              '&:hover': { bgcolor: '#f8fafc' },
                              '& .MuiButton-startIcon': { mr: 1 },
                            }}
                          >
                            <Typography
                              sx={{
                                fontFamily: 'Inter, sans-serif',
                                fontWeight: selected ? 700 : 400,
                                fontSize: { xs: '14px', sm: '15px', md: '16px' },
                                lineHeight: 1.4,
                                color: '#1f2937',
                              }}
                            >
                              {source.label}
                            </Typography>
                          </Button>
                        );
                      })
                    )}
                  </Box>
                </Box>
              )}
            </Box>
          </ClickAwayListener>
        </Box>

        <Box sx={{ borderTop: '1px solid #e4e7ec', display: 'flex', flexDirection: 'column', flex: '1 1 auto', minHeight: 0 }}>
          <Box sx={{ maxHeight: { xs: '40vh', sm: '48vh', md: '56vh' }, overflowY: 'auto', flex: '1 1 auto', minHeight: 0 }}>
            {filteredQuestions.length === 0 ? (
              <Box sx={{ px: { xs: 1.5, sm: 2, md: 2.5 }, py: { xs: 3, sm: 4, md: 5 } }}>
                <Typography
                  sx={{
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 500,
                    fontSize: { xs: '13px', sm: '14px' },
                    color: '#98a2b3',
                  }}
                >
                  No questions found for this source.
                </Typography>
              </Box>
            ) : (
              filteredQuestions.map((question, index) => {
                const selected = selectedIdSet.has(question.id);
                const dependsOnAll = question.dependsOnQuestionIds ?? [];
                const dependsOnAny = question.dependsOnAnyQuestionIds ?? [];
                const unmetAll = dependsOnAll.filter((id) => !selectedIdSet.has(id));
                const anySatisfied = dependsOnAny.length === 0 || dependsOnAny.some((id) => selectedIdSet.has(id));
                const isDisabledByDeps =
                  !selected &&
                  (unmetAll.length > 0 || (dependsOnAny.length > 0 && !anySatisfied));
                const copyDisabled = isSubmitting || isDisabledByDeps;

                return (
                  <Box key={question.id} sx={{ px: { xs: 1.5, sm: 2, md: 2.5 } }}>
                    <Box
                      sx={{
                        py: { xs: 1.5, sm: 1.75, md: 2.25 },
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                        gap: { xs: 1.25, sm: 1.5, md: 2 },
                      }}
                    >
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', gap: { xs: 0.75, sm: 1.25 } }}>
                          <Typography
                            sx={{
                              fontFamily: 'Inter, sans-serif',
                              fontWeight: 500,
                              fontSize: { xs: '12px', sm: '13px', md: '14px' },
                              lineHeight: 1.35,
                              color: '#98a2b3',
                            }}
                          >
                            {question.code}
                          </Typography>
                          <Typography
                            sx={{
                              fontFamily: 'Inter, sans-serif',
                              fontWeight: 400,
                              fontSize: { xs: '14px', sm: '16px', md: '18px' },
                              lineHeight: 1.4,
                              color: isDisabledByDeps ? '#98a2b3' : '#1f2937',
                            }}
                          >
                            {question.questionText}
                          </Typography>
                          <Typography
                            sx={{
                              fontFamily: 'Inter, sans-serif',
                              fontWeight: 500,
                              fontSize: { xs: '12px', sm: '14px', md: '16px' },
                              lineHeight: 1.35,
                              color: '#98a2b3',
                            }}
                          >
                            • {question.typeLabel}
                          </Typography>
                        </Box>

                        {question.chips.length > 0 && (
                          <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {question.chips.map((chip) => (
                              <Tag
                                key={`${question.id}-${chip}`}
                                variant="neutral"
                                size="sm"
                                className="border-b border-b-[#E8DECA] bg-[#F6EDDC] font-[Inter,sans-serif] text-xs font-semibold text-[#6D5F43]"
                                sx={{ height: { xs: 24, sm: 26, md: 28 } }}
                              >
                                {chip}
                              </Tag>
                            ))}
                          </Box>
                        )}

                        {isDisabledByDeps && (
                          <Typography
                            sx={{
                              mt: 1,
                              fontFamily: 'Inter, sans-serif',
                              fontWeight: 400,
                              fontSize: { xs: '12px', sm: '12px' },
                              lineHeight: 1.4,
                              color: '#98a2b3',
                            }}
                          >
                            Select the required question(s) above first — this one is shown only when those are answered.
                          </Typography>
                        )}
                      </Box>

                      <Tooltip
                        title={
                          isDisabledByDeps
                            ? 'Select the required question(s) in this list first'
                            : selected
                              ? 'Remove from selection'
                              : 'Add to selection'
                        }
                      >
                        <span>
                          <Button
                            variant="outlined"
                            onClick={() => !copyDisabled && handleToggleQuestion(question.id)}
                            disabled={copyDisabled}
                            startIcon={selected ? <CheckRoundedIcon sx={{ fontSize: 18 }} /> : <AddRoundedIcon sx={{ fontSize: 18 }} />}
                            sx={{
                              minWidth: { xs: 100, sm: 110, md: 126 },
                              height: { xs: 38, sm: 42, md: 44 },
                              borderRadius: { xs: '10px', sm: '12px' },
                              borderColor: selected ? '#24b8b3' : '#d0d5dd',
                              bgcolor: selected ? '#ecfbfa' : '#f8fafc',
                              color: selected ? '#05958f' : isDisabledByDeps ? '#98a2b3' : '#344054',
                              fontFamily: 'Inter, sans-serif',
                              fontWeight: 600,
                              fontSize: { xs: '13px', sm: '14px', md: '16px' },
                              textTransform: 'none',
                              flexShrink: 0,
                              '&:hover': {
                                borderColor: selected ? '#16a9a4' : '#c2c8d0',
                                bgcolor: selected ? '#e1f8f6' : '#f2f4f7',
                              },
                              ...(isDisabledByDeps && {
                                cursor: 'not-allowed',
                                pointerEvents: 'auto',
                              }),
                            }}
                          >
                            {selected ? 'Selected' : 'Copy'}
                          </Button>
                        </span>
                      </Tooltip>
                    </Box>
                    {index < filteredQuestions.length - 1 && <Box sx={{ borderBottom: '1px solid #e4e7ec' }} />}
                  </Box>
                );
              })
            )}
          </Box>

          <Box
            sx={{
              px: { xs: 1.5, sm: 2, md: 2.5 },
              py: { xs: 1.5, sm: 1.75, md: 2 },
              borderTop: '1px solid #e4e7ec',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1.5,
              bgcolor: '#fff',
              flexShrink: 0,
            }}
          >
            <Typography
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontWeight: 400,
                fontSize: { xs: '12px', sm: '13px', md: '14px' },
                lineHeight: 1.4,
                color: '#667085',
              }}
            >
              {selectedQuestions.length} questions selected
            </Typography>

            <Button
              variant="contained"
              onClick={() => onDone(selectedQuestions)}
              disabled={isSubmitting || selectedQuestions.length === 0}
              sx={{
                minWidth: { xs: 88, sm: 100, md: 112 },
                height: { xs: 40, sm: 44, md: 48 },
                borderRadius: { xs: '12px', sm: '14px', md: '16px' },
                bgcolor: '#25a8da',
                fontFamily: 'Inter, sans-serif',
                fontWeight: 600,
                fontSize: { xs: '14px', sm: '15px', md: '16px' },
                textTransform: 'none',
                boxShadow: 'none',
                '&:hover': { bgcolor: '#1498ca', boxShadow: 'none' },
              }}
            >
              {isSubmitting ? 'Saving...' : 'Done'}
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
